import { Checkbox, Col, Form, FormRow, InputError } from '@op/opds.opux';
import { Combobox, ComboboxOption } from '@op/opds.opux-combobox';
import { useEffect } from 'react';
import { Trans } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import styles from './BeneficiarySelect.module.scss';
import NamedBeneficiaries from './NamedBeneficiaries';
import { selectSelectedPremiums } from '../../../state/selectors/coverageSelectors';
import { AnalyticsPhases, trackAction } from 'api/analytics-api';
import { BeneficiaryType } from 'dto/beneficiary';
import i18n from 'i18n';
import { CustomTile } from 'individual/components/common/CustomTile';
import MoreLessWCAG from 'individual/components/common/MoreLessWCAG';
import { useAppStore } from 'state/store';
import { Feature } from 'types/featureSwitches';
import { isFeatureEnabled } from 'utils';

export default function BeneficiarySelect() {
  const options = Object.keys(BeneficiaryType);

  const setSelectedBeneficiary = useAppStore(
    (state) => state.setSelectedBeneficiary
  );
  const setMaritalRightsExclusion = useAppStore(
    (state) => state.setMaritalRightsExclusion
  );
  const { errors, selectedBeneficiary, maritalRightsExclusion } = useAppStore(
    useShallow((state) => ({
      errors: state.errors,
      selectedBeneficiary: state.selectedBeneficiary,
      maritalRightsExclusion: state.maritalRightsExclusion,
    }))
  );
  const selectedPremiums = useAppStore(selectSelectedPremiums);

  const requireMaritalRightsForSpouse = isFeatureEnabled(
    Feature.FEATURE_REQUIRE_MARITAL_RIGHTS_FOR_SPOUSE
  );

  useEffect(() => {
    if (
      selectedBeneficiary === BeneficiaryType.DEATH_ESTATE ||
      (!requireMaritalRightsForSpouse &&
        selectedBeneficiary === BeneficiaryType.SPOUSE)
    )
      setMaritalRightsExclusion(false);
  }, [
    selectedBeneficiary,
    setMaritalRightsExclusion,
    requireMaritalRightsForSpouse,
  ]);

  const formBeneficiaryOptionAriaLabel = ({ data }: any): string => {
    return data.title;
  };

  const error: string = errors?.beneficiaryType;
  const isInvalid = error !== undefined && !selectedBeneficiary;
  const errorId = 'beneficiary-error-id';

  return (
    <CustomTile className={styles.infoHeaders}>
      <h2>{i18n.t('beneficiarySelect.header')}</h2>
      <p>{i18n.t('beneficiarySelect.description')}</p>
      <MoreLessWCAG
        messages={{
          more: i18n.t('beneficiarySelect.more'),
          less: i18n.t('beneficiarySelect.less'),
        }}
        screenReaderMessages={{
          more: i18n.t('beneficiarySelect.more'),
          less: i18n.t('moreLessScreenReaderMessages.less.beneficiaries'),
        }}>
        <p>{i18n.t('beneficiarySelect.hint1')}</p>
        <h3>{i18n.t('beneficiarySelect.SPOUSE')}</h3>
        <Trans
          i18nKey={'beneficiarySelect.SPOUSE_info'}
          components={{ bold: <strong /> }}
        />
        <h3>{i18n.t('beneficiarySelect.SPOUSE_AND_CHILDREN')}</h3>
        <Trans
          i18nKey={'beneficiarySelect.SPOUSE_AND_CHILDREN_info'}
          components={{ bold: <strong /> }}
        />
        <h3>{i18n.t('beneficiarySelect.CHILDREN')}</h3>
        <p>{i18n.t('beneficiarySelect.CHILDREN_info')}</p>
        <h3>{i18n.t('beneficiarySelect.NEXT_OF_KIN')}</h3>
        <p>{i18n.t('beneficiarySelect.NEXT_OF_KIN_info')}</p>
        <h3>{i18n.t('beneficiarySelect.DEATH_ESTATE')}</h3>
        <p>{i18n.t('beneficiarySelect.DEATH_ESTATE_info')}</p>
        <h3>{i18n.t('beneficiarySelect.NAMED_BENEFICIARY')}</h3>
        <p>{i18n.t('beneficiarySelect.NAMED_BENEFICIARY_info')}</p>
      </MoreLessWCAG>
      <Form stacked>
        <FormRow label={i18n.t('beneficiarySelect.label')} required>
          <Combobox
            id='beneficiaryType'
            value={selectedBeneficiary}
            invalid={isInvalid}
            placeholder={i18n.t('beneficiarySelect.chooseOption')}
            data-testid='beneficiary-type-select'
            onChange={(value) => {
              const beneficiary = value.toString() as BeneficiaryType;
              setSelectedBeneficiary(beneficiary);
              trackAction(
                'edunsaaja-clicked',
                'button',
                AnalyticsPhases.BENEFICIARIES_AND_DOCUMENTS,
                {
                  coveragePremiums: useAppStore.getState().coveragePremiums,
                  selectedSumInsuredPremiums: selectedPremiums,
                  beneficiaryInfo: beneficiary,
                  selectedCoverages: useAppStore.getState().selectedCoverages,
                }
              );
            }}
            formatOptionAriaLabel={formBeneficiaryOptionAriaLabel}
            buttonProps={{
              'aria-describedby': isInvalid ? errorId : undefined,
            }}>
            {options.map((option) => (
              <ComboboxOption
                key={option}
                value={option}
                data-testid={`${option.toLowerCase().replace('_', '-')}-beneficiary-option`}
                optionData={{ title: i18n.t('beneficiarySelect.' + option) }}>
                {i18n.t('beneficiarySelect.' + option)}
              </ComboboxOption>
            ))}
          </Combobox>
          {error && !selectedBeneficiary && (
            <InputError
              error={i18n.t(`beneficiarySelect.${error}`)}
              id={errorId}
              data-testid={errorId}
            />
          )}
        </FormRow>
        {selectedBeneficiary && (
          <>
            <h3>{i18n.t('beneficiarySelect.' + selectedBeneficiary)}</h3>
            <p>
              <Trans
                i18nKey={'beneficiarySelect.' + selectedBeneficiary + '_info'}
                components={{ bold: <strong /> }}
              />
            </p>
          </>
        )}
        {selectedBeneficiary !== BeneficiaryType.NAMED_BENEFICIARY &&
          selectedBeneficiary !== BeneficiaryType.DEATH_ESTATE &&
          (requireMaritalRightsForSpouse ||
            selectedBeneficiary !== BeneficiaryType.SPOUSE) && (
            <FormRow className={styles.shrinkFont}>
              <Checkbox
                data-testid='marital-rights-exclusion-checkbox'
                checked={maritalRightsExclusion}
                onChange={(event, checked) => {
                  setMaritalRightsExclusion(checked);
                }}>
                {i18n.t('beneficiarySelect.maritalRightsExclusion')}
              </Checkbox>
              <Col className={styles.maritalRightsMore}>
                <MoreLessWCAG
                  className={styles.moreLess}
                  messages={{
                    more: i18n.t(
                      'beneficiarySelect.moreAboutMaritalRightsExclusion'
                    ),
                    less: i18n.t('beneficiarySelect.less'),
                  }}
                  screenReaderMessages={{
                    more: i18n.t(
                      'beneficiarySelect.moreAboutMaritalRightsExclusion'
                    ),
                    less: i18n.t(
                      'moreLessScreenReaderMessages.less.maritalRightsExclusion'
                    ),
                  }}>
                  {i18n.t(
                    'beneficiarySelect.maritalRightsExclusionMoreDescription'
                  )}
                </MoreLessWCAG>
              </Col>
            </FormRow>
          )}
        {selectedBeneficiary === BeneficiaryType.NAMED_BENEFICIARY && (
          <NamedBeneficiaries />
        )}
      </Form>
    </CustomTile>
  );
}
